import { useEffect, useRef, useState } from "react"
import { Card } from "./ui/card"
import { Crosshair, MapPin, Loader2, AlertTriangle } from "lucide-react"
import { DataService } from "../services/data.service"
import { LocateFix, LocateLastKnown, LocateStatusResponse } from "../models/locate.model"
import { DateTime } from "luxon"
import L from "leaflet"
import "leaflet/dist/leaflet.css"

export interface LocateNowProps {
    // null = legacy access exists but no modern person_user_id. Render the
    // soft "not enabled" panel without firing a locate request that would 403.
    beneficiaryUserId: number | null
    timezone: string
}

// Translate backend failure codes to caregiver-readable text. Unknown codes
// fall through to a sanitised version of the raw reason so a new server-side
// failure mode still produces something legible without a PWA redeploy.
function translateFailure(reason: string | undefined): string {
    switch (reason) {
        case 'permission_denied':
            return "Phone doesn't have location permission"
        case 'location_disabled':
        case 'services_disabled':
            return 'Phone has location services turned off'
        case 'no_push_token':
        case 'no_device':
            return "Phone isn't reachable for location requests right now"
        case 'background_denied':
            return "Phone can't get a fix in the background"
    }
    if (!reason) return 'Locate failed'
    return reason.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())
}

function ageLabel(ts: number): string {
    const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000))
    if (diffSec < 60) return `${diffSec}s ago`
    const min = Math.floor(diffSec / 60)
    if (min < 60) return `${min} min ago`
    const h = Math.floor(min / 60)
    const remMin = min % 60
    if (h < 24) return remMin ? `${h}h ${remMin}m ago` : `${h}h ago`
    const d = Math.floor(h / 24)
    return `${d}d ago`
}

function accuracyLabel(acc: number | undefined): string {
    if (acc == null || !isFinite(acc) || acc <= 0) return ''
    return ` · ±${Math.round(acc)}m`
}

function sourceLabel(source: string | undefined): string {
    if (source === 'walk_point') return 'from recent walk'
    if (source === 'locate') return 'from last Find Now'
    return source ? `from ${source}` : ''
}

export function LocateNow(props: LocateNowProps) {
    const [status, setStatus] = useState<LocateStatusResponse | null>(null)
    const [requesting, setRequesting] = useState(false)
    const [elapsedSec, setElapsedSec] = useState(0)
    const [error, setError] = useState<string | null>(null)
    // Caregiver has legacy access to this deployment but no modern consent
    // record on wellnuo-backend — soft state, not a hard error. Surfaced via
    // the 'access_denied' sentinel from DataService.requestLocate/getLocateStatus.
    const [accessDenied, setAccessDenied] = useState(false)

    const startedAtRef = useRef<number | null>(null)
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const cancelRef = useRef(false)

    const mapRef = useRef<HTMLDivElement | null>(null)
    const mapInstance = useRef<L.Map | null>(null)
    const layerRef = useRef<L.LayerGroup | null>(null)

    const isPending = status?.status === 'pending' || (requesting && !status)

    function stopTimers() {
        if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null }
        if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null }
    }

    useEffect(() => {
        // Cancel any in-flight poll if the component unmounts or the
        // beneficiary changes underneath us — a stale poll firing on a new
        // beneficiary would render the wrong fix.
        return () => {
            cancelRef.current = true
            stopTimers()
        }
    }, [])

    useEffect(() => {
        // Reset state when switching beneficiaries.
        cancelRef.current = true
        stopTimers()
        setStatus(null)
        setError(null)
        setAccessDenied(false)
        setElapsedSec(0)
        setRequesting(false)
        startedAtRef.current = null
        cancelRef.current = false
    }, [props.beneficiaryUserId])

    async function pollOnce(requestId: string) {
        if (props.beneficiaryUserId == null) return
        try {
            const rsp = await DataService.getLocateStatus(props.beneficiaryUserId, requestId)
            if (cancelRef.current) return
            setStatus(rsp)
            if (rsp.status === 'pending') {
                pollTimerRef.current = setTimeout(() => pollOnce(requestId), 2000)
            } else {
                stopTimers()
            }
        } catch (e: any) {
            if (cancelRef.current) return
            if (e?.message === 'access_denied') setAccessDenied(true)
            else setError(e?.message || 'Locate request failed')
            stopTimers()
        }
    }

    async function startLocate() {
        if (isPending) return
        if (props.beneficiaryUserId == null) {
            // No modern person_user_id → render the soft state without firing.
            setAccessDenied(true)
            return
        }
        const beneficiaryUserId = props.beneficiaryUserId
        setError(null)
        setAccessDenied(false)
        setStatus(null)
        setElapsedSec(0)
        setRequesting(true)
        cancelRef.current = false
        startedAtRef.current = Date.now()
        elapsedTimerRef.current = setInterval(() => {
            if (startedAtRef.current) setElapsedSec(Math.floor((Date.now() - startedAtRef.current) / 1000))
        }, 1000)
        try {
            const { requestId } = await DataService.requestLocate(beneficiaryUserId)
            if (cancelRef.current) return
            // Seed status as pending so the UI doesn't flicker before the first poll.
            setStatus({ requestId, status: 'pending' })
            await pollOnce(requestId)
        } catch (e: any) {
            if (cancelRef.current) return
            if (e?.message === 'access_denied') setAccessDenied(true)
            else setError(e?.message || 'Could not start locate request')
            stopTimers()
        } finally {
            if (!cancelRef.current) setRequesting(false)
        }
    }

    // Pick the best point to render on the map: prefer the fresh fix, then
    // fall back to lastKnown (which the backend supplies even mid-pending so
    // the caregiver isn't staring at a blank map while the phone wakes up).
    const renderedPoint: (LocateFix & { isFresh: boolean; source?: string }) | null = (() => {
        if (status?.status === 'complete' && status.fix) return { ...status.fix, isFresh: true }
        const lk: LocateLastKnown | undefined = status?.lastKnown
        if (lk) return { ...lk, isFresh: false }
        return null
    })()

    useEffect(() => {
        if (!renderedPoint || !mapRef.current) return

        // The map container unmounts whenever renderedPoint goes null
        // (between Find-Now invocations: startLocate sets status=null → no
        // fix → no lastKnown → renderedPoint=null → div unmounts). React
        // remounts a NEW <div> when renderedPoint becomes truthy again, but
        // mapInstance.current still points at the previous Leaflet map
        // whose container is now detached. Skipping reinit and adding
        // markers/setView to that phantom map silently no-ops — the user
        // sees "Located Xs ago" but a blank box. Detect the mismatch and
        // tear down before reinit.
        if (mapInstance.current && mapInstance.current.getContainer() !== mapRef.current) {
            mapInstance.current.remove()
            mapInstance.current = null
            layerRef.current = null
        }

        if (!mapInstance.current) {
            mapInstance.current = L.map(mapRef.current, { zoomControl: true })
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '© OpenStreetMap contributors'
            }).addTo(mapInstance.current)
            layerRef.current = L.layerGroup().addTo(mapInstance.current)
        }

        const map = mapInstance.current!
        const layer = layerRef.current!
        layer.clearLayers()

        const latlng: [number, number] = [renderedPoint.lat, renderedPoint.lng]
        const color = renderedPoint.isFresh ? '#2563eb' : '#9ca3af'
        L.circleMarker(latlng, { radius: 9, color: '#fff', fillColor: color, fillOpacity: 1, weight: 2 }).addTo(layer)
        if (renderedPoint.acc && renderedPoint.acc > 0) {
            L.circle(latlng, { radius: renderedPoint.acc, color, fillColor: color, fillOpacity: 0.15, weight: 1 }).addTo(layer)
        }

        const zoom = renderedPoint.acc && renderedPoint.acc > 200 ? 14 : 17
        map.setView(latlng, zoom)
        setTimeout(() => map.invalidateSize(), 50)
    }, [renderedPoint?.lat, renderedPoint?.lng, renderedPoint?.acc, renderedPoint?.isFresh])

    useEffect(() => {
        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove()
                mapInstance.current = null
                layerRef.current = null
            }
        }
    }, [])

    const statusKind = status?.status

    return (
        <Card className="p-4 space-y-3 shadow-md rounded-lg border" id="locate_now">
            <div className="flex justify-between items-center border-b pb-2">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <Crosshair className="mr-2 text-gray-600 bg-gray-100 p-2 rounded-lg text-3xl" size={32} /> Find Now
                </h3>
            </div>

            <button
                onClick={startLocate}
                disabled={isPending}
                className={`w-full py-3 rounded-lg text-base font-semibold text-white transition ${
                    isPending ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                }`}
            >
                {isPending ? (
                    <span className="inline-flex items-center justify-center gap-2">
                        <Loader2 className="animate-spin" size={18} />
                        Locating... {elapsedSec}s
                    </span>
                ) : statusKind === 'complete' ? 'Find again' : statusKind ? 'Try again' : 'Find phone'}
            </button>

            {accessDenied && (
                <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2">
                    Find Now isn't enabled for this beneficiary yet. Ask them to install the WellNuo app and grant location-sharing consent.
                </div>
            )}

            {error && !accessDenied && (
                <div className="text-sm text-red-600 flex items-center gap-2 bg-red-50 rounded-lg p-2">
                    <AlertTriangle size={16} /> {error}
                </div>
            )}

            {statusKind === 'complete' && status?.fix && (
                <div className="text-sm text-gray-700 bg-green-50 rounded-lg p-2 flex items-center gap-2">
                    <MapPin size={16} className="text-green-700" />
                    Located {ageLabel(status.fix.ts)}{accuracyLabel(status.fix.acc)}
                </div>
            )}

            {statusKind === 'failed' && (
                <div className="text-sm text-red-700 bg-red-50 rounded-lg p-2 flex items-center gap-2">
                    <AlertTriangle size={16} /> {translateFailure(status?.reason)}
                </div>
            )}

            {statusKind === 'timeout' && (
                <div className="text-sm text-amber-800 bg-amber-50 rounded-lg p-2 flex items-center gap-2">
                    <AlertTriangle size={16} /> Couldn't reach the phone right now.
                </div>
            )}

            {/* lastKnown banner: shown whenever the backend returned one — usually
                on timeout, sometimes mid-pending so caregivers aren't staring at a
                blank map while the silent push wakes up the phone. */}
            {status?.lastKnown && statusKind !== 'complete' && (
                <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2">
                    Last known: {ageLabel(status.lastKnown.ts)}{accuracyLabel(status.lastKnown.acc)} {sourceLabel(status.lastKnown.source)}
                </div>
            )}

            {renderedPoint && (
                <div className="border rounded-lg overflow-hidden">
                    <div ref={mapRef} style={{ height: 260, width: '100%' }} />
                </div>
            )}
        </Card>
    )
}
